// @ts-strict-ignore
import { isAfter, startOfDay } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { type SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { ENVIRONMENT_CONFIG } from 'constants/environment';
import { LoginStatus } from 'constants/login-status';
import { CONTEXT25_CAMPAIGN_DISMISSED_KEY } from 'constants/notification-bar';
import { TopBarNotificationType } from 'constants/notifications';
import { anyToBoolean } from 'helpers/boolean';
import { isDesktopAppDetected } from 'helpers/desktop-app/is-detected';
import { isDesktopView, isMobileView } from 'helpers/device';
import { shouldShowVisitorLimitNotification } from 'helpers/visitor-limit-exceeded';
import { PartnerType } from 'interfaces/entities/current-license';
import type { IBrowserNotificationsProps, IVisitorLimitExceededProps } from 'interfaces/notification-bar';
import { type ISubscription } from 'interfaces/subscription';
import { PlatformNamespace } from 'services/connectivity/configuration-api/properties/constants';
import { type PlatformNamespaceProperties } from 'services/connectivity/configuration-api/properties/types';
import {
  collapseNotificationBar,
  collapseNotificationBarWithoutAnimation,
  expandNotificationBar,
} from 'services/notifications-bar';
import { getItem } from 'services/session-storage';
import { RequestAction } from 'store/entities/actions';
import { getLoggedInAgentStatus } from 'store/entities/agents/selectors';
import {
  IntegrationLicensePropertiesActions,
  IntegrationLicensePropertiesActionNames,
} from 'store/entities/integration-license-properties/actions';
import { getIntegrationLicenseProperties } from 'store/entities/integration-license-properties/selectors';
import { SubscriptionActionNames } from 'store/entities/subscription/actions';
import {
  getIsSubscriptionInTrial,
  getIsSubscriptionManagedByShopify,
  getSubscription,
  getSubscriptionSeats,
} from 'store/entities/subscription/selectors';
import { AGENT_CUSTOM_PROPERTIES } from 'store/features/agent-custom-properties/actions';
import {
  getDesktopAppInstalled,
  getDownloadDesktopAppNextVisibilityDate,
  getEliminateLegacyStarterPlanBannerSeen,
  getHasPendingInvites,
  hasFetchedAgentCustomProperties,
} from 'store/features/agent-custom-properties/selectors';
import { getHadChatsRecently, getIsCodeInstalled } from 'store/features/code-installation/selectors';

import {
  getCanFetchSubscription,
  getCanManageSubscription,
  getIsStarterPlan,
  getPartnerType,
  getVisitorLimitExceeded,
} from '../session/selectors';

import { NotificationsBarActions, NotificationsBarActionsNames } from './actions';
import { shouldShowBrowserNotificationsBar } from './helpers';
import { hasMobileNotifications, hasNotifications, isNotificationBarVisible } from './selectors';

const CONTEXT25_CAMPAIGN_START_DATE = '2025-10-02';
const CONTEXT25_CAMPAIGN_END_DATE = '2025-10-31T23:59:59.999Z';

function* expand(): SagaIterator {
  const isVisible = yield select(isNotificationBarVisible);

  if (!isVisible) {
    return;
  }

  const shouldExpandOnDesktop = yield select(hasNotifications);
  const shouldExpandOnMobile = yield select(hasMobileNotifications);
  const shouldExpand = (isMobileView() && shouldExpandOnMobile) || (isDesktopView() && shouldExpandOnDesktop);

  if (!shouldExpand) {
    return;
  }

  expandNotificationBar();
}

function* handleBrowserNotificationBar(): SagaIterator {
  const showBrowserNotificationsBar = shouldShowBrowserNotificationsBar();
  const isCodeInstalled = yield select(getIsCodeInstalled);

  if (showBrowserNotificationsBar && isCodeInstalled) {
    const barType = showBrowserNotificationsBar as TopBarNotificationType;
    const props: IBrowserNotificationsProps = { type: barType };
    yield put(
      NotificationsBarActions.showNotificationsBar({
        name: TopBarNotificationType.BrowserNotifications,
        props,
      }),
    );
  }
}

function* handleInstallCodeNotificationBar(): SagaIterator {
  const isCodeInstalled = yield select(getIsCodeInstalled);
  const hadChatsRecently = yield select(getHadChatsRecently);

  if (hadChatsRecently) {
    return;
  }

  if (!isCodeInstalled) {
    yield put(
      NotificationsBarActions.showNotificationsBar({
        name: TopBarNotificationType.InstallCode,
      }),
    );
  }
}

function* handleAwayStatusNotificationBar(): SagaIterator {
  const status: LoginStatus = yield select(getLoggedInAgentStatus);

  if (status === LoginStatus.Away) {
    yield put(NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.StatusAway }));
  }
}

function* handlePendingInvitesNotificationBar(): SagaIterator {
  const areAgentCustomPropertiesFetched: boolean = yield select(hasFetchedAgentCustomProperties);
  if (!areAgentCustomPropertiesFetched) {
    yield take(AGENT_CUSTOM_PROPERTIES.FETCH_AGENT_CUSTOM_PROPERTIES[RequestAction.SUCCESS]);
  }

  const hasPendingInvites = yield select(getHasPendingInvites);
  if (hasPendingInvites) {
    yield put(NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.SendPendingInvites }));
  }
}

export function* handleDownloadDesktopAppNotificationBar(): SagaIterator {
  if (isDesktopAppDetected()) {
    return;
  }

  const areAgentCustomPropertiesFetched: boolean = yield select(hasFetchedAgentCustomProperties);
  if (!areAgentCustomPropertiesFetched) {
    yield take(AGENT_CUSTOM_PROPERTIES.FETCH_AGENT_CUSTOM_PROPERTIES[RequestAction.SUCCESS]);
  }

  const desktopAppInstalled = yield select(getDesktopAppInstalled);
  const nextVisibilityDate = yield select(getDownloadDesktopAppNextVisibilityDate);
  const today = new Date();

  if (!anyToBoolean(desktopAppInstalled) && (!nextVisibilityDate || isAfter(today, startOfDay(nextVisibilityDate)))) {
    yield put(NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.DownloadDesktopApp }));
  }
}

export function* handleEliminateLegacyStarterPlanBar(): SagaIterator {
  // TODO: Admin users can't fetch subscription, so this wait may block indefinitely
  // Consider checking canFetchSubscription before waiting (similar to handleVisitorLimitExceededBar)
  const subscription: ISubscription = yield select(getSubscription);

  if (isEmpty(subscription)) {
    yield take(SubscriptionActionNames.SUBSCRIPTION_FETCHED);
  }

  const hasSeenBanner = yield select(getEliminateLegacyStarterPlanBannerSeen);
  const isTrial = yield select(getIsSubscriptionInTrial);
  const canManageSubscription = yield select(getCanManageSubscription);
  const isStarterPlan = yield select(getIsStarterPlan);
  const isReseller = (yield select(getPartnerType)) === PartnerType.Reseller;
  const isShopifyLicense = yield select(getIsSubscriptionManagedByShopify);
  const subscriptionQuantity = yield select(getSubscriptionSeats);

  if (
    !hasSeenBanner &&
    !isTrial &&
    !isShopifyLicense &&
    !isReseller &&
    isStarterPlan &&
    canManageSubscription &&
    subscriptionQuantity > 1
  ) {
    yield put(
      NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.EliminateLegacyStarterPlan }),
    );
  }
}

export function checkContext25CampaignPeriod(): {
  now: number;
  inPeriod: boolean;
  shouldCleanup: boolean;
} {
  const now = Date.now();
  const campaignStart = new Date(CONTEXT25_CAMPAIGN_START_DATE).getTime();
  const campaignEnd = new Date(CONTEXT25_CAMPAIGN_END_DATE).getTime();

  const inPeriod = now >= campaignStart && now <= campaignEnd;
  const shouldCleanup = now > campaignEnd;

  return { now, inPeriod, shouldCleanup };
}

function* handleContext25CampaignBar(): SagaIterator {
  // Skip promotional campaigns in test environments for predictable testing
  if (ENVIRONMENT_CONFIG.arePromotionalCampaignsDisabled) {
    return;
  }

  // Check if banner was already dismissed in this session
  const isDismissed = Boolean(getItem<boolean>(CONTEXT25_CAMPAIGN_DISMISSED_KEY));
  if (isDismissed) {
    return;
  }

  // Wait for subscription data to be loaded before checking trial status
  // TODO: Admin users can't fetch subscription, so this wait may block indefinitely
  // Consider checking canFetchSubscription before waiting and using getIsOnTrial as fallback
  // (similar to handleVisitorLimitExceededBar)
  const subscription: ISubscription = yield select(getSubscription);
  if (isEmpty(subscription)) {
    yield take(SubscriptionActionNames.SUBSCRIPTION_FETCHED);
  }

  // Only show banner to paid users (exclude trial users)
  const isTrial: boolean = yield select(getIsSubscriptionInTrial);
  if (isTrial) {
    return;
  }

  const { inPeriod, shouldCleanup } = checkContext25CampaignPeriod();

  if (shouldCleanup) {
    // Hide any currently visible campaign banner for long-lived sessions
    yield put(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.Context25Campaign));

    return;
  }

  if (!inPeriod) return;

  // Banner shows on every app session during campaign period (Oct 2-31)
  // Component handles its own display logic and impression tracking
  yield put(NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.Context25Campaign }));
}

function* ensureCdpNamespaceExists(): SagaIterator {
  let integrationLicenseProperties: PlatformNamespaceProperties = yield select(getIntegrationLicenseProperties);

  if (integrationLicenseProperties?.[PlatformNamespace.CDP]) {
    return;
  }

  if (isEmpty(integrationLicenseProperties)) {
    yield take(IntegrationLicensePropertiesActionNames.FETCH_SUCCESS);
    integrationLicenseProperties = yield select(getIntegrationLicenseProperties);
  }

  if (!integrationLicenseProperties?.[PlatformNamespace.CDP]) {
    yield put(
      IntegrationLicensePropertiesActions.fetchForNamespace({
        namespace: PlatformNamespace.CDP,
      }),
    );
    yield take(IntegrationLicensePropertiesActionNames.FETCH_SINGLE_NAMESPACE_SUCCESS);
  }
}

export function* handleVisitorLimitExceededBar(): SagaIterator {
  const canFetchSubscription = yield select(getCanFetchSubscription);
  const subscription: ISubscription = yield select(getSubscription);

  if (isEmpty(subscription) && canFetchSubscription) {
    yield take(SubscriptionActionNames.SUBSCRIPTION_FETCHED);
  }

  yield call(ensureCdpNamespaceExists);

  const visitorLimitExceeded: ReturnType<typeof getVisitorLimitExceeded> = yield select(getVisitorLimitExceeded);

  if (visitorLimitExceeded) {
    const shouldShow = shouldShowVisitorLimitNotification(visitorLimitExceeded.count);

    if (shouldShow) {
      const props: IVisitorLimitExceededProps = {
        count: visitorLimitExceeded.count,
      };
      yield put(
        NotificationsBarActions.showNotificationsBar({
          name: TopBarNotificationType.VisitorLimitExceeded,
          props,
        }),
      );
    }
  }
}

function* initializeSaga(): SagaIterator {
  yield all([
    call(handleBrowserNotificationBar),
    call(handleInstallCodeNotificationBar),
    call(handleAwayStatusNotificationBar),
    call(handlePendingInvitesNotificationBar),
    call(handleDownloadDesktopAppNotificationBar),
    call(handleEliminateLegacyStarterPlanBar),
    call(handleContext25CampaignBar),
    call(handleVisitorLimitExceededBar),
  ]);

  yield put(NotificationsBarActions.toggleDisplayNotificationsBar());
  yield call(expand);
}

function collapseWithoutAnimation(): void {
  collapseNotificationBarWithoutAnimation();
}

function* hideNotification(): SagaIterator {
  const hasNotificationsOnDesktop = yield select(hasNotifications);
  const hasNotificationsOnMobile = yield select(hasMobileNotifications);
  const shouldCollapse =
    (isMobileView() && !hasNotificationsOnMobile) || (isDesktopView() && !hasNotificationsOnDesktop);

  if (!shouldCollapse) {
    /*
    Trigger expand as fallback to handle hidden topbar (even if we have notifications to display) when browser tab is hidden for some time
    */
    yield call(expand);

    return;
  }

  collapseNotificationBar();
  yield call(expand);
}

export function* notificationsBarSaga(): SagaIterator {
  yield takeLatest('APP_READY', initializeSaga);
  yield takeEvery(NotificationsBarActionsNames.HIDE_NOTIFICATIONS_BAR, hideNotification);
  yield takeEvery(NotificationsBarActionsNames.HIDE_ALL_NOTIFICATIONS_BAR, collapseWithoutAnimation);
  yield takeEvery(
    [NotificationsBarActionsNames.SHOW_NOTIFICATIONS_BAR, NotificationsBarActionsNames.SHOW_ALL_NOTIFICATIONS_BAR],
    expand,
  );
}
